/**
 * 移动端客户细分管理页
 */
import React, { useEffect, useState } from 'react';
import {
  List,
  PullToRefresh,
  Empty,
  Button,
  Dialog,
  Form,
  Input,
  Picker,
  Tag,
  Switch,
  Skeleton,
} from 'antd-mobile';
import { adminCustomerSegmentApi, adminCustomerTagApi } from '../../api/index';
import { isNestedRules, stringifyRuleTree, normalizeToTree } from '../../utils/segmentRules';
import Toast from 'antd-mobile/es/components/toast';
import { useNavigate } from 'react-router-dom';

// 规则字段配置
const CONDITION_FIELDS = [
  { label: '总消费金额', value: 'totalSpent' },
  { label: '订单数量', value: 'orderCount' },
  { label: '距上次订单天数', value: 'daysSinceLastOrder' },
  { label: '城市', value: 'city' },
  { label: '省/州', value: 'province' },
  { label: '国家', value: 'country' },
  { label: '客户标签', value: 'tags' },
];

// 操作符配置（数字类型）
const NUMBER_OPERATORS = [
  { label: '大于等于', value: 'gte' },
  { label: '大于', value: 'gt' },
  { label: '小于等于', value: 'lte' },
  { label: '小于', value: 'lt' },
  { label: '等于', value: 'eq' },
];

// 操作符配置（字符串类型）
const STRING_OPERATORS = [
  { label: '等于', value: 'eq' },
  { label: '不等于', value: 'neq' },
  { label: '包含', value: 'contains' },
];

// 操作符配置（标签类型）
const TAG_OPERATORS = [
  { label: '包含此标签', value: 'contains' },
  { label: '不包含此标签', value: 'not_contains' },
];

export default function MobileCustomerSegmentListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [form] = Form.useForm();
  const [segmentType, setSegmentType] = useState('dynamic');
  const [conditions, setConditions] = useState([
    { field: 'totalSpent', operator: 'gte', value: '' }
  ]);
  const [matchLogic, setMatchLogic] = useState('AND');
  // 是否因嵌套规则而锁定条件编辑区
  const [nestedLocked, setNestedLocked] = useState(false);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchSegments();
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const data = await adminCustomerTagApi.list();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('加载标签列表失败', err);
    }
  };

  const fetchSegments = async () => {
    try {
      const data = await adminCustomerSegmentApi.list();
      setSegments(data || []);
    } catch (err) {
      Toast.show('获取细分列表失败');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await fetchSegments();
  };

  const openForm = (segment) => {
    setEditingSegment(segment);
    if (segment) {
      setSegmentType(segment.type);
      form.setFieldsValue({
        name: segment.name,
        description: segment.description,
        type: segment.type,
      });
      if (segment.rules) {
        try {
          const parsed = JSON.parse(segment.rules);
          if (isNestedRules(parsed)) {
            // 嵌套规则：锁定条件编辑区，不填充扁平 conditions
            setNestedLocked(true);
          } else {
            setNestedLocked(false);
            // 用 normalizeToTree 兼容老扁平格式和新 group 格式
            const tree = normalizeToTree(parsed);
            setConditions(tree.children.map((c) => ({ field: c.field, operator: c.operator, value: c.value })));
            setMatchLogic(tree.logic);
          }
        } catch (e) {
          // 解析失败：解锁，恢复默认条件
          setNestedLocked(false);
          setConditions([{ field: 'totalSpent', operator: 'gte', value: '' }]);
        }
      } else {
        setNestedLocked(false);
      }
    } else {
      // 新建：确保不锁定条件编辑区
      setNestedLocked(false);
      setSegmentType('dynamic');
      setConditions([{ field: 'totalSpent', operator: 'gte', value: '' }]);
      setMatchLogic('AND');
      form.resetFields();
    }
    setFormModalVisible(true);
  };

  const handleAddCondition = () => {
    setConditions([...conditions, { field: 'totalSpent', operator: 'gte', value: '' }]);
  };

  const handleRemoveCondition = (index) => {
    if (conditions.length === 1) {
      Toast.show('至少保留一个条件');
      return;
    }
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleUpdateCondition = (index, field, value) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setConditions(newConditions);
  };

  const handleSubmit = async (values) => {
    try {
      let rules = null;
      if (segmentType === 'dynamic') {
        if (nestedLocked && editingSegment) {
          // 嵌套规则保存时直接保留原始 rules，不用扁平 conditions 重新序列化
          rules = editingSegment.rules;
        } else {
          const validConditions = conditions.filter(c => c.value !== '');
          rules = JSON.stringify({
            conditions: validConditions,
            matchLogic: matchLogic,
          });
        }
      }

      const data = {
        ...values,
        type: segmentType,
        rules,
      };

      if (editingSegment) {
        await adminCustomerSegmentApi.update(editingSegment.id, data);
        Toast.show('更新成功');
      } else {
        await adminCustomerSegmentApi.create(data);
        Toast.show('创建成功');
      }
      setFormModalVisible(false);
      fetchSegments();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '操作失败');
    }
  };

  const handleDelete = (id) => {
    Dialog.confirm({
      content: '确定要删除此细分吗？',
      onConfirm: async () => {
        try {
          await adminCustomerSegmentApi.delete(id);
          Toast.show('删除成功');
          fetchSegments();
        } catch (err) {
          Toast.show(err?.response?.data?.message || '删除失败');
        }
      },
    });
  };

  const handleRefresh = async (id) => {
    try {
      await adminCustomerSegmentApi.refresh(id);
      Toast.show('已刷新');
      fetchSegments();
    } catch (err) {
      Toast.show('刷新失败');
    }
  };

  // 导出客户
  const handleExport = async (segment) => {
    try {
      Toast.show({ icon: 'loading', content: '正在导出...' });
      const blob = await adminCustomerSegmentApi.exportCustomers(segment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `客户细分_${segment.name.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_')}.csv`;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      Toast.show('客户已导出');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '导出失败');
    }
  };

  const handleViewCustomers = (segment) => {
    navigate(`/admin/m/customer-segments/${segment.id}/customers`);
  };

  // 将规则字符串转换为可读摘要，兼容嵌套和扁平格式
  const ruleText = (rulesStr) => {
    if (!rulesStr) return '无规则';
    try {
      return stringifyRuleTree(normalizeToTree(JSON.parse(rulesStr)), {}) || '无规则';
    } catch (e) {
      return '规则解析失败';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={3} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>客户细分</h2>
        <Button size="small" color="primary" onClick={() => openForm(null)}>
          新建
        </Button>
      </div>

      <PullToRefresh onRefresh={refreshData}>
        {segments.length > 0 ? (
          <List>
            {segments.map((segment) => (
              <List.Item
                key={segment.id}
                style={{
                  background: '#fff',
                  marginBottom: 12,
                  padding: '12px 16px',
                }}
                description={
                  <div style={{ fontSize: 13, color: '#999', marginTop: 4 }}>
                    <div style={{ marginBottom: 4 }}>
                      {segment.description || '无描述'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span>客户数：<strong style={{ color: '#1677ff' }}>{segment.customerCount || 0}</strong></span>
                      {segment.type === 'dynamic' && segment.rules && (
                        <span style={{ color: '#999' }}>
                          | {ruleText(segment.rules)}
                        </span>
                      )}
                    </div>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {segment.type === 'dynamic' && (
                      <span
                        style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
                        onClick={() => handleRefresh(segment.id)}
                      >
                        刷新
                      </span>
                    )}
                    <span
                      style={{ fontSize: 13, color: '#52c41a', cursor: 'pointer' }}
                      onClick={() => handleViewCustomers(segment)}
                    >
                      客户
                    </span>
                    <span
                      style={{ fontSize: 13, color: '#faad14', cursor: 'pointer' }}
                      onClick={() => handleExport(segment)}
                    >
                      导出
                    </span>
                    <span
                      style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => openForm(segment)}
                    >
                      编辑
                    </span>
                    <span
                      style={{ fontSize: 13, color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(segment.id);
                      }}
                    >
                      删除
                    </span>
                  </div>
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{segment.name}</div>
                  <Tag color={segment.type === 'dynamic' ? 'blue' : 'green'} size="small">
                    {segment.type === 'dynamic' ? '动态' : '静态'}
                  </Tag>
                </div>
              </List.Item>
            ))}
          </List>
        ) : (
          <div style={{ padding: '80px 0' }}>
            <Empty description="暂无细分" />
          </div>
        )}
      </PullToRefresh>

      {/* 新建/编辑弹窗 */}
      <Dialog
        visible={formModalVisible}
        style={{ '--content-width': '90%', '--content-max-height': '90%' }}
        content={
          <div style={{ padding: '16px 0', maxHeight: '70vh', overflow: 'auto' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {editingSegment ? '编辑细分' : '新建细分'}
            </h3>
            <Form
              form={form}
              layout="vertical"
              footer={
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <Button block type="submit" color="primary" onClick={() => form.submit()}>
                    保存
                  </Button>
                  <Button block variant="outline" onClick={() => setFormModalVisible(false)}>
                    取消
                  </Button>
                </div>
              }
              onFinish={handleSubmit}
            >
              <Form.Item name="name" label="细分名称" rules={[{ required: true }]}>
                <Input placeholder="例：高价值客户" />
              </Form.Item>
              <Form.Item name="description" label="细分描述">
                <Input placeholder="描述该细分的特征" />
              </Form.Item>
              <Form.Item name="type" label="细分类型" initialValue="dynamic">
                <Picker
                  value={[segmentType]}
                  onChange={(val) => setSegmentType(val[0])}
                  options={[
                    { label: '动态细分 - 根据规则自动匹配', value: 'dynamic' },
                    { label: '静态细分 - 手动添加客户', value: 'static' },
                  ]}
                >
                  {(items) => <div>{items[0]?.label || '请选择'}</div>}
                </Picker>
              </Form.Item>

              {segmentType === 'dynamic' && (
                nestedLocked ? (
                  /* 嵌套规则：只读提示 + 摘要，禁用条件编辑 */
                  <div style={{ padding: '8px 0', color: '#999', fontSize: 13 }}>
                    该规则包含条件分组，请在电脑端编辑。<br />
                    <span style={{ color: '#666' }}>
                      {editingSegment && editingSegment.rules
                        ? (() => {
                            try {
                              return stringifyRuleTree(normalizeToTree(JSON.parse(editingSegment.rules)), {});
                            } catch (e) {
                              return '规则解析失败';
                            }
                          })()
                        : '无规则'}
                    </span>
                  </div>
                ) : (
                  /* 原有扁平条件编辑区保持不变 */
                  <>
                    <Form.Item label="匹配逻辑">
                      <Picker
                        value={matchLogic}
                        onChange={(val) => setMatchLogic(val[0])}
                        options={[
                          { label: '满足所有条件 (AND)', value: 'AND' },
                          { label: '满足任一条件 (OR)', value: 'OR' },
                        ]}
                      >
                        {(items) => <div>{items[0]?.label || '请选择'}</div>}
                      </Picker>
                    </Form.Item>

                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>匹配条件</div>
                      {conditions.map((condition, index) => {
                        const fieldConfig = CONDITION_FIELDS.find(f => f.value === condition.field);
                        const isTagField = fieldConfig?.value === 'tags';
                        const operators = isTagField ? TAG_OPERATORS : (fieldConfig?.value === 'totalSpent' || fieldConfig?.value === 'orderCount' || fieldConfig?.value === 'daysSinceLastOrder' ? NUMBER_OPERATORS : STRING_OPERATORS);

                        return (
                          <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                            <Picker
                              value={[condition.field]}
                              onChange={(val) => handleUpdateCondition(index, 'field', val[0])}
                              options={CONDITION_FIELDS}
                              style={{ flex: 2 }}
                            >
                              {(items) => <div>{items[0]?.label || '字段'}</div>}
                            </Picker>
                            <Picker
                              value={[condition.operator]}
                              onChange={(val) => handleUpdateCondition(index, 'operator', val[0])}
                              options={operators}
                              style={{ flex: 1.5 }}
                            >
                              {(items) => <div>{items[0]?.label || '操作'}</div>}
                            </Picker>
                            {isTagField ? (
                              <Picker
                                value={[condition.value]}
                                onChange={(val) => handleUpdateCondition(index, 'value', val[0])}
                                options={tags.map(tag => ({
                                  label: tag.name,
                                  value: tag.name,
                                }))}
                                style={{ flex: 1.5 }}
                                placeholder="选择标签"
                              >
                                {(items) => <div>{items[0]?.label || '选择标签'}</div>}
                              </Picker>
                            ) : (
                              <Input
                                value={condition.value}
                                onChange={(val) => handleUpdateCondition(index, 'value', val)}
                                placeholder="数值"
                                style={{ flex: 1.5 }}
                              />
                            )}
                            <Button
                              size="small"
                              color="danger"
                              fill="none"
                              onClick={() => handleRemoveCondition(index)}
                            >
                              ×
                            </Button>
                          </div>
                        );
                      })}
                      <Button size="small" onClick={handleAddCondition}>
                        + 添加条件
                      </Button>
                    </div>
                  </>
                )
              )}

              {segmentType === 'static' && (
                <div style={{ fontSize: 13, color: '#999' }}>
                  静态细分需要手动添加客户。创建细分后，在客户列表页面可以选择客户添加到该细分。
                </div>
              )}
            </Form>
          </div>
        }
        closeOnMaskClick={false}
      />
    </div>
  );
}
